import React from 'react'
import { Timeline, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { Avatar, Request, RequestUserData } from 'src/types'
import * as CSS from './styles'

type UserHistory = {
  justification: string
  status: string
  traveler: Avatar
  updated_on: number
}

function getStatusColor(status: string) {
  switch (status.toUpperCase()) {
    case 'APPROVAL_PENDING':
    case 'APPROVED':
    case 'CLOSING_PENDING':
    case 'CONFERENCE_PENDING':
    case 'FINISHED':
    case 'UNDERWAY':
      return 'green'
    case 'REPROVED':
    case 'APPROVAL_REPROVED':
    case 'CONFERENCE_REPROVED':
    case 'CLOSING_REPROVED':
      return 'red'
    case 'CANCELED':
    default:
      return 'gray'
  }
}

function getUserDataHistory(acc: UserHistory[], cur: RequestUserData) {
  if (cur.request_approvals?.length) {
    cur.request_approvals.forEach(approval =>
      approval.request_approval_records.forEach(record => {
        if (record.justification) {
          acc.push({
            justification: record.justification,
            status: record.status,
            traveler: record.approver,
            updated_on: record.updated_on
          })
        }
      })
    )
  }

  if (cur.request_emission?.request_emission_id) {
    cur.request_emission.request_emission_records.forEach(record => {
      if (record.justification) {
        acc.push({
          justification: record.justification,
          status: record.status,
          traveler: record.issuer,
          updated_on: record.updated_on
        })
      }
    })
  }

  if (cur.request_conferences?.length) {
    cur.request_conferences.forEach(conference =>
      conference.request_conference_records.forEach(record => {
        if (record.justification) {
          acc.push({
            justification: record.justification,
            status: record.status,
            traveler: record.checker,
            updated_on: record.updated_on
          })
        }
      })
    )
  }

  if (cur.request_closings?.length) {
    cur.request_closings.forEach(closing =>
      closing.request_closing_records.forEach(record => {
        if (record.justification) {
          acc.push({
            justification: record.justification,
            status: record.status,
            traveler: record.controller,
            updated_on: record.updated_on
          })
        }
      })
    )
  }

  return acc
}

interface Props {
  request: Request
}

export default function HistoryJustificationSummary({ request }: Props) {
  const { t } = useTranslation()

  const requestJustification: UserHistory[] = []

  if (request.request_number) {
    requestJustification.push({
      justification: t('common.request-created'),
      status: 'APPROVED',
      traveler: request.requester,
      updated_on: request.requested_on
    })
  }

  if (request.justification) {
    requestJustification.push({
      justification: request.justification,
      status: request?.status,
      traveler: request.requester,
      updated_on: request.requested_on
    })
  }

  const historyJustification = request.request_user_data.reduce(
    getUserDataHistory,
    requestJustification
  )

  if (!historyJustification.length) {
    return <></>
  }

  historyJustification.sort(({ updated_on: a }, { updated_on: b }) =>
    a > b ? 1 : a < b ? -1 : 0
  )

  return (
    <CSS.Card
      data-test-id="summary-justification-history"
      title="web.summary.justification-history"
    >
      <Timeline>
        {historyJustification.map((history, index) => (
          <Timeline.Item key={index} color={getStatusColor(history.status)}>
            <CSS.DateTime>
              {moment.unix(history.updated_on).utc().format('DD/MM/YYYY')}
              {', '}
            </CSS.DateTime>
            <CSS.Traveler strong>{history.traveler?.name}</CSS.Traveler>
            <Typography.Text>
              {' - '}
              {history.justification}
            </Typography.Text>
          </Timeline.Item>
        ))}
      </Timeline>
    </CSS.Card>
  )
}
